/* Uses https://www.npmjs.com/package/react-image-crop
 *
 * Implementation example reference from: https://codesandbox.io/p/sandbox/react-image-crop-demo-with-react-hooks-y831o
 * - Last seen 2025-05-07
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography as Text,
} from '@mui/material';
import { useTranslation, withTranslation } from 'react-i18next';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';

import 'react-image-crop/dist/ReactCrop.css';

import { useWorkspaceActions } from '../../../actions/WorkspaceActions';

const DEFAULT_ASPECT = 16 / 9;
const MIN_CROP_HEIGHT = 50;
const TO_RADIANS = Math.PI / 180;

function centerAspectCrop(width, height, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height),
    width,
    height
  );
}

function drawPreview(img, canvas, crop, scale = 1, angle = 0) {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  const scaleX = img.naturalWidth / img.width;
  const scaleY = img.naturalHeight / img.height;
  const pixelRatio = window.devicePixelRatio;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);
  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;
  const angleRads = angle * TO_RADIANS;
  const centerX = img.naturalWidth / 2;
  const centerY = img.naturalHeight / 2;

  ctx.save();

  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(angleRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    img,
    0,
    0,
    img.naturalWidth,
    img.naturalHeight,
    0,
    0,
    img.naturalWidth,
    img.naturalHeight
  );

  ctx.restore();
}

function useDebounceEffect(fn, waitTime, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

const useTransformImageDialog = ({ data, onClose }) => {
  const { src, workspace } = data;
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [uploading, setUploading] = useState(false);
  const { updateWorkspaceMeetingBotSettingsImage } =
    useWorkspaceActions(workspace);

  const save = useCallback(async () => {
    const img = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!img || !previewCanvas || !completedCrop)
      throw new Error('Crop canvas does not exist');

    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    const ctx = offscreen.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );
    const blob = await offscreen.convertToBlob({ type: 'image/png' });
    const formData = new FormData();
    formData.append('file', blob);
    setUploading(true);
    updateWorkspaceMeetingBotSettingsImage({
      form: formData,
      onProgress: (e) => {
        if (e.progress !== 1) return;
        setUploading(false);
      },
    });
    onClose();
  }, [onClose, completedCrop, updateWorkspaceMeetingBotSettingsImage]);

  useDebounceEffect(() => {
    if (
      !(
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      )
    )
      return;
    drawPreview(imgRef.current, previewCanvasRef.current, completedCrop);
  }, [completedCrop]);

  const showAspect = false;

  return {
    src,
    showAspect,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    uploading,
    imgRef,
    previewCanvasRef,
    save,
  };
};

const ImageCrop = forwardRef(
  (
    {
      crop,
      setCrop,
      onComplete,
      aspect = DEFAULT_ASPECT,
      scale = 1,
      angle = 0,
      src,
    },
    imgRef
  ) => {
    const onImageLoad = useCallback(
      (e) => {
        if (!aspect) return;

        const { width, height } = e.currentTarget;
        setCrop(centerAspectCrop(width, height, aspect));
      },
      [aspect, setCrop]
    );

    return (
      <div>
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={onComplete}
          aspect={aspect}
          minHeight={MIN_CROP_HEIGHT}
        >
          <img
            ref={imgRef}
            alt="broken img src"
            src={src}
            style={{ transform: `scale(${scale}) rotate(${angle}deg)` }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      </div>
    );
  }
);

const Preview = forwardRef(({ width, height }, ref) => {
  const { t } = useTranslation();
  return (
    <div>
      <Text>{t('Förhandsvisning')}:</Text>
      <canvas
        ref={ref}
        style={{
          border: '1px solid black',
          objectFit: 'contain',
          width,
          height,
        }}
      />
    </div>
  );
});

const CustomDialogTitle = withTranslation()(({ t }) => (
  <DialogTitle>{t('Redigera assistentens bild')}</DialogTitle>
));

const CancelButton = withTranslation()(({ t, onClick }) => (
  <Button variant="outlined" color="error" onClick={onClick}>
    {t('Avbryt')}
  </Button>
));

const SaveButton = withTranslation()(({ t, onClick }) => (
  <Button variant="contained" onClick={onClick}>
    {t('Spara')}
  </Button>
));

/**# Transform an image in a dialog
 *
 * ## Properties:
 * data.src - the image source */
export default function TransformImageDialog({ open, onClose, data }) {
  const {
    src,
    imgRef,
    previewCanvasRef,
    crop,
    setCrop,
    completedCrop,
    setCompletedCrop,
    save,
  } = useTransformImageDialog({ data, onClose });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle />
      <DialogContent>
        <Stack gap={1} alignItems="center">
          {!!src && (
            <ImageCrop
              ref={imgRef}
              crop={crop}
              setCrop={setCrop}
              onComplete={(c) => setCompletedCrop(c)}
              src={src}
            />
          )}
          {!!completedCrop && (
            <Preview
              ref={previewCanvasRef}
              width={completedCrop.width}
              height={completedCrop.height}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton onClick={save} />
      </DialogActions>
    </Dialog>
  );
}
